import FVector from '@data/geometry/FVector';
import { PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import {
    BoxDesignState,
    DesignSchemeSetType,
    DesignSchemeState,
    FloorBasedDesignState,
    GlobalDesignSchemeState,
    LayerKind,
    OrientedVolumeDesignState,
    RendererBaseConfig,
    SchemeKey,
} from '@visualizer/types';
import Layer from 'Simulations/Layer';
import { getGrid } from 'store/LoadingStore';
import { DesignSchemeSet } from './DesignSchemeSet';
import { requireResources } from 'store/assertions';
import useNotificationStore from '@gui/store/notification';
import { NotificationType } from '@visualizer/types';

export class DesignSchemeSetManager {
    private static instance: DesignSchemeSetManager;
    private designSchemeSets: DesignSchemeSet[] = [];

    constructor(state: GlobalDesignSchemeState) {
        this.designSchemeSets = [];

        // 1. Loop through sets in Zustand and re-generate everything (no writing on Zustand --> No use of controller)
        for (const [setId, setData] of Object.entries(state.sets)) {
            // If the DS Set doesn't belong to this grid, skip it:
            if (requireResources().gridId != setData.grid) continue;

            // Create the runtime DesignSchemeSet without initializing Zustand
            const runtimeSet = new DesignSchemeSet(setData.name, setData.type);
            runtimeSet.setID(setId);
            runtimeSet.setvisibility(setData.visibility);

            // 2. Rebuild footprints
            for (const [fpId, fpState] of Object.entries(setData.footprints)) {
                const newVectorArray = [];
                for (let v of fpState.polygon) newVectorArray.push(new FVector(v.x, v.y, v.z));
                runtimeSet.addFootprint(newVectorArray, fpId);

                // 3. Rebuild design schemes for this footprint
                for (const [schemeId, schemeState] of Object.entries(fpState.designSchemes)) {
                    const key = schemeState.schemeKey;

                    // ---- Build linkedLayers map here ----
                    const linkedLayers = new Map<LayerKind, Layer>();

                    if (schemeState.linkedLayerIds) {
                        for (const [kindString, layerId] of Object.entries(schemeState.linkedLayerIds)) {
                            const kind = Number(kindString) as LayerKind; // LayerKind is an enum → numeric keys

                            try {
                                const layer = getGrid().searchLayerByUID(layerId);
                                if (layer) linkedLayers.set(kind, layer);
                            } catch (e) {
                                useNotificationStore
                                    .getState()
                                    .showNotification(
                                        `The layer associated with "${runtimeSet.getName()}" is not available anymore, current active layer has been set instead`,
                                        NotificationType.Warning
                                    );
                            }
                        }
                    }

                    // Now create and add the design scheme
                    const rendererConfig = this.getConfig(key, schemeState);
                    const scheme = runtimeSet.addDesignScheme(fpId, key, rendererConfig!, linkedLayers);
                    scheme.setID(schemeId);
                }
            }
            this.designSchemeSets.push(runtimeSet);
        }
    }

    getConfig(
        type: SchemeKey,
        state: DesignSchemeState
    ): RendererBaseConfig | FloorBasedDesignState | OrientedVolumeDesignState | BoxDesignState | undefined {
        switch (type) {
            case SchemeKey.FLOOR_BASED:
                return state.floorBased;
            case SchemeKey.CONVEX_HULL:
                return state.convexHull;
            case SchemeKey.ORIENTED_VOLUME:
                return state.orientedVolume;
            case SchemeKey.BOX:
                return state.box;
        }
    }

    static getInstance(state: GlobalDesignSchemeState): DesignSchemeSetManager {
        if (!DesignSchemeSetManager.instance) {
            DesignSchemeSetManager.instance = new DesignSchemeSetManager(state);
        }
        return DesignSchemeSetManager.instance;
    }

    addProject(name: string, type: DesignSchemeSetType): DesignSchemeSet {
        const project = new DesignSchemeSet(name, type);
        this.designSchemeSets.push(project);
        return project;
    }

    getProjects(): DesignSchemeSet[] {
        return this.designSchemeSets;
    }

    getProjectById(id: string): DesignSchemeSet | undefined {
        return this.designSchemeSets.find((p) => p.getID() === id);
    }

    removeProject(id: string): void {
        this.designSchemeSets = this.designSchemeSets.filter((p) => p.getID() !== id);
    }

    update(pipelineResult: PipelineExecutionResult, layerId: string): void {
        this.designSchemeSets.forEach((dsSet) => dsSet.update(pipelineResult, layerId));
    }

    getDesignSchemeByID(schemeID: string) {
        return this.designSchemeSets.map((set) => set.getDesignSchemeByID(schemeID)).find((scheme) => scheme !== undefined);
    }
}
